import axios from 'axios'
import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { env } from './env'

const api = axios.create({
  baseURL: env.apiBaseUrl,
})

// Endpoints that need Accept-Language header for i18n translations
const I18N_ENDPOINTS = [
  '/api/actions',
  '/api/matieres',
  '/api/types-activite',
  '/api/periodes',
  '/api/programmes',
]

// Request interceptor: attach JWT token + Accept-Language
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  if (config.url && I18N_ENDPOINTS.some((ep) => config.url?.includes(ep))) {
    const language = localStorage.getItem('language') || 'fr'
    config.headers['Accept-Language'] = language.startsWith('fr') ? 'fr' : 'en'
  }

  return config
})

// ── Refresh token logic (mutex to avoid parallel refreshes) ──
let isRefreshing = false
let refreshQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = []

function getRefreshToken(): string | null {
  return localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken')
}

function isRefreshTokenPersistent(): boolean {
  return !!localStorage.getItem('refreshToken')
}

function storeRefreshToken(token: string, persistent: boolean) {
  if (persistent) {
    localStorage.setItem('refreshToken', token)
    sessionStorage.removeItem('refreshToken')
  } else {
    sessionStorage.setItem('refreshToken', token)
    localStorage.removeItem('refreshToken')
  }
}

function clearAllTokens() {
  localStorage.removeItem('token')
  localStorage.removeItem('refreshToken')
  sessionStorage.removeItem('refreshToken')
}

async function doRefresh(): Promise<string> {
  const rt = getRefreshToken()
  if (!rt) throw new Error('No refresh token')

  const persistent = isRefreshTokenPersistent()
  // Use raw axios to avoid interceptor loop
  const response = await axios.post(`${env.apiBaseUrl}/auth/refresh`, { refreshToken: rt })
  const data = response.data

  if (data.result === 'SUCCESS' && data.data) {
    localStorage.setItem('token', data.data.token)
    storeRefreshToken(data.data.refreshToken, persistent)
    return data.data.token
  }
  throw new Error('Refresh failed')
}

// Response interceptor: auto-refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // Don't intercept /api/users/me (handled by AuthContext) or /auth/* endpoints
    const url = originalRequest?.url || ''
    if (
      error.response?.status !== 401 ||
      url === '/api/users/me' ||
      url.startsWith('/auth/') ||
      originalRequest._retry
    ) {
      return Promise.reject(error)
    }

    const rt = getRefreshToken()
    if (!rt) {
      clearAllTokens()
      window.location.href = '/login'
      return Promise.reject(error)
    }

    if (isRefreshing) {
      // Queue this request until the refresh completes
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (newToken: string) => {
            originalRequest._retry = true
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            resolve(api(originalRequest))
          },
          reject,
        })
      })
    }

    isRefreshing = true
    originalRequest._retry = true

    try {
      const newToken = await doRefresh()

      // Retry queued requests
      refreshQueue.forEach(({ resolve }) => resolve(newToken))
      refreshQueue = []

      // Retry the original request
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      return api(originalRequest)
    } catch {
      // Refresh failed → clear everything and redirect to login
      refreshQueue.forEach(({ reject }) => reject(error))
      refreshQueue = []
      clearAllTokens()
      window.location.href = '/login'
      return Promise.reject(error)
    } finally {
      isRefreshing = false
    }
  },
)

export default api
